import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Check, CheckCircle2, CreditCard, ShieldCheck } from "lucide-react";

import { api, uploadFile } from "@/lib/api";
import { callbackUrl, redirectToPaystack } from "@/lib/checkout";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { BackHeader } from "@/components/back-header";
import { SelfieCamera } from "@/components/selfie-camera";
import { Button, Checkbox, ChipGroup, ErrorState, Loading, TestModeBanner } from "@/components/ui";

const STEPS = ["Paid", "Documents submitted", "Under review", "Decision"];
const stepIndex = (a) => (!a ? -1 : a.verification_status === "paid" ? 0 : a.verification_status === "under_review" ? 2 : 3);

function DocSlot({ kind, label, optional, done, busy, preview, onClick }) {
  return (
    <button data-testid={`verification-upload-${kind}`} onClick={onClick} disabled={!!busy}
      className={`mb-3 flex w-full items-center gap-4 rounded-[20px] border-2 bg-white p-4 text-left transition-shadow hover:shadow-md disabled:opacity-60 ${done ? "border-[#3A6C54]/50" : "border-dashed border-[#D48D93]"}`}>
      {preview ? <img src={preview} alt="" className="h-14 w-14 rounded-xl object-cover" /> : (
        <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#F6E3E4]">{kind === "selfie" ? <Camera className="h-6 w-6 text-primary" /> : <CreditCard className="h-6 w-6 text-primary" />}</span>
      )}
      <span className="flex-1">
        <span className="block text-[15px] font-bold">{label}{optional ? " (optional)" : ""}</span>
        <span className="block text-xs text-muted-foreground">{busy === kind ? "Encrypting & uploading…" : done ? "Uploaded · click to replace" : kind === "selfie" ? "Take a live selfie with your camera" : "Choose a clear photo"}</span>
      </span>
      {done ? <CheckCircle2 className="h-[22px] w-[22px] text-[#3A6C54]" /> : null}
    </button>
  );
}

export default function Verification() {
  const toast = useToast();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const [terms, setTerms] = useState(false);
  const [method, setMethod] = useState([]);
  const [checkout, setCheckout] = useState(null);
  const [busy, setBusy] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [previews, setPreviews] = useState({});
  const fileInput = useRef(null);
  const pendingKind = useRef(null);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["verification"], queryFn: () => api("/verification") });
  const reload = () => ["verification", "dashboard", "notifications", "unread"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  // Local previews are in-memory object URLs only — revoked on unmount, never persisted.
  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  useEffect(() => () => Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u)), []);

  const pay = async () => {
    if (!terms) return toast("Please accept the verification terms", "error");
    if (!method.length) return toast("Choose a payment method", "error");
    setBusy("pay");
    try {
      const res = await api("/verification/payments", { method: "POST", body: { method: data.payment_methods.find((m) => m.label === method[0]).id, accept_terms: true, channel: "web", callback_url: callbackUrl() } });
      if (!redirectToPaystack(res)) setCheckout({ reference: res.reference }); // no Paystack key → labelled TEST MODE checkout
    } catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };
  const complete = async (outcome) => {
    setBusy(outcome);
    try {
      const res = await api(`/verification/payments/${checkout.reference}/test-complete`, { method: "POST", body: { outcome } });
      setCheckout(null);
      if (res.payment.status === "success") toast("Payment confirmed. Now upload your documents.", "success");
      else toast("Payment failed. No documents can be uploaded until payment succeeds.", "error");
      reload();
    } catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };
  const upload = async (kind, file) => {
    setBusy(kind);
    try {
      await uploadFile("/verification/documents", file, { kind }, `${kind}.jpg`);
      setPreviews((p) => ({ ...p, [kind]: URL.createObjectURL(file) }));
      toast("Uploaded securely", "success");
      reload();
    } catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };
  const pickId = (kind) => { pendingKind.current = kind; fileInput.current?.click(); };
  const submit = async () => {
    setBusy("submit");
    try { await api("/verification/submit", { method: "POST" }); toast("Submitted for review", "success"); reload(); }
    catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };

  if (isLoading) return <><BackHeader title="Get Verified" /><Loading /></>;
  if (isError || !data) return <><BackHeader title="Get Verified" /><ErrorState testId="verification-error" message={error?.message ?? ""} onRetry={() => refetch()} /></>;
  const a = data.application;
  const verified = data.member_verification_status === "verified" || a?.verification_status === "verified";
  const canUpload = !!a && ["paid", "more_info_needed", "rejected"].includes(a.verification_status);
  const idx = verified ? 3 : stepIndex(a);
  const req = data.requirements ?? {};

  return (
    <div data-testid="verification-screen" className="mx-auto max-w-3xl">
      <BackHeader title="Get Verified" />
      <input ref={fileInput} type="file" accept="image/*" className="hidden" data-testid="verification-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(pendingKind.current, f); }} />
      <div className={`rounded-[20px] border-2 bg-white p-6 ${verified ? "border-[#C19B53]" : "border-border"}`}>
        {verified ? <CheckCircle2 className="h-9 w-9 text-[#C19B53]" /> : <ShieldCheck className="h-9 w-9 text-primary" />}
        <h2 className="mt-2 text-[28px] leading-tight" data-testid="verification-status-title">{verified ? "You are a Verified Member" : "Stand out with a Verified Member badge"}</h2>
        <p className="mt-1 text-sm text-muted-foreground">Verified members get a gold badge and a boost in matches.</p>
      </div>
      {a || verified ? (
        <ol className="mt-6 grid gap-3 rounded-[20px] border border-border bg-white p-5 sm:grid-cols-4" data-testid="verification-tracker">
          {STEPS.map((s, i) => {
            const label = i === 3 ? (verified ? "Verified" : a?.verification_status === "rejected" ? "Rejected" : a?.verification_status === "more_info_needed" ? "More info needed" : "Verified / Rejected") : s;
            const on = i <= idx || (i === 1 && idx >= 2);
            return (
              <li key={s} className="flex items-center gap-2">
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${on ? "bg-primary" : "border-2 border-border"}`}>{on ? <Check className="h-3.5 w-3.5 text-white" /> : null}</span>
                <span data-testid={`verification-step-${i}`} className={`text-sm ${on ? "font-bold" : "text-muted-foreground"} ${i === 3 && on && !verified ? "text-[#9E3C3E]" : ""}`}>{label}</span>
              </li>
            );
          })}
        </ol>
      ) : null}
      {a?.review_notes && ["rejected", "more_info_needed"].includes(a.verification_status) ? (
        <div className="mt-6 rounded-[20px] bg-[#F6E3E4] p-5" data-testid="verification-review-notes">
          <p className="font-bold">{a.verification_status === "rejected" ? "Why it wasn’t approved" : "What we need"}</p>
          <p className="mt-1 text-sm">{a.review_notes}</p>
          <p className="mt-1 text-sm">Re-upload below and submit again — no new fee needed.</p>
        </div>
      ) : null}
      {!a && !verified ? (
        <div className="mt-8">
          <h2 className="mb-3 text-[24px]">How it works</h2>
          {["Pay the one-time verification fee", "Upload your government ID (front, optional back)", "Take a live selfie with your camera", "Our team reviews within 48 hours"].map((t, i) => (
            <p key={t} className="mb-2 flex items-center gap-3 text-sm"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F6E3E4] text-xs font-bold text-primary">{i + 1}</span>{t}</p>
          ))}
          <div className="my-6 rounded-[20px] border border-[#C19B53]/60 bg-white p-5 text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Verification fee</p>
            <p className="font-display text-[44px] font-bold" data-testid="verification-fee">{data.fee_display}</p>
            <p className="text-xs text-muted-foreground">One-time · {data.currency}</p>
          </div>
          <p className="mb-6 text-xs leading-5 text-muted-foreground" data-testid="verification-declaration">{data.declaration}</p>
          <h3 className="mb-3 font-display text-[22px]">Payment method</h3>
          <ChipGroup testIdPrefix="verification-method" multi={false} options={data.payment_methods.map((m) => m.label)} value={method} onChange={setMethod} />
          <div className="mt-5"><Checkbox testId="verification-terms-checkbox" checked={terms} onToggle={() => setTerms(!terms)}>I agree to the verification terms and understand the fee is for the review process, not a guaranteed approval.</Checkbox></div>
          {data.test_mode ? (
            <>
              <TestModeBanner testId="verification-test-mode-banner" />
              <p className="mt-2 text-xs text-muted-foreground" data-testid="verification-test-mode-note">Online payments (Paystack) aren't connected yet, so checkout is simulated.</p>
            </>
          ) : <p className="text-xs text-muted-foreground" data-testid="verification-paystack-note">You'll be redirected to Paystack's secure checkout, then brought back here.</p>}
          <Button testId="verification-pay-button" title={data.test_mode ? `Test pay ${data.fee_display}` : `Pay ${data.fee_display} with Paystack`} loading={busy === "pay"} onClick={pay} className="mt-4 w-full sm:w-auto sm:min-w-[240px]" />
        </div>
      ) : null}
      {canUpload ? (
        <div className="mt-8">
          <h2 className="mb-2 text-[24px]">Upload your documents</h2>
          {a.payment_status === "waived" ? <p className="mb-2 text-sm" data-testid="verification-fee-waived">Verification fee included with your Yearly membership — a reviewer still needs to approve your documents.</p> : null}
          <p className="mb-4 text-xs text-muted-foreground">Files are encrypted before storage and only visible to LOVE NET verification reviewers.</p>
          <DocSlot kind="id_front" label="Government ID — front" done={a.documents.id_front} busy={busy} preview={previews.id_front} onClick={() => pickId("id_front")} />
          <DocSlot kind="id_back" label="Government ID — back" optional={!req.id_back_required} done={a.documents.id_back} busy={busy} preview={previews.id_back} onClick={() => pickId("id_back")} />
          <DocSlot kind="selfie" label="Live selfie" optional={req.selfie_required === false} done={a.documents.selfie} busy={busy} preview={previews.selfie} onClick={() => setCamOpen(true)} />
          <Button testId="verification-submit-button" title="Submit for review" loading={busy === "submit"} onClick={submit} className="mt-2 w-full sm:w-auto sm:min-w-[240px]"
            disabled={!a.documents.id_front || (req.selfie_required !== false && !a.documents.selfie) || (!!req.id_back_required && !a.documents.id_back)} />
        </div>
      ) : null}
      {a?.verification_status === "under_review" ? <p className="mt-6 rounded-xl bg-[#F4EBE6] p-4 text-sm" data-testid="verification-under-review">Your documents are under review. Your badge stays “Unverified” until a reviewer approves your application.</p> : null}
      {verified ? <Button testId="verification-done-button" title="Refresh my profile" variant="outline" className="mt-6" onClick={() => refresh()} /> : null}
      <Modal open={!!checkout} onClose={() => setCheckout(null)} title="TEST MODE checkout" testId="verification-checkout-sheet">
        <TestModeBanner />
        <p className="mt-3 text-sm">Reference {checkout?.reference}. Choose an outcome to simulate the payment provider.</p>
        <div className="mt-6 flex flex-col gap-3">
          <Button testId="checkout-success-button" title="Simulate success" loading={busy === "success"} onClick={() => complete("success")} />
          <Button testId="checkout-fail-button" title="Simulate failure" variant="outline" loading={busy === "fail"} onClick={() => complete("fail")} />
        </div>
      </Modal>
      <SelfieCamera open={camOpen} onClose={() => setCamOpen(false)} onCapture={(f) => { setCamOpen(false); upload("selfie", f); }} />
    </div>
  );
}
